import { ShippingMethod } from '@/types/ShippingMethods';

/**
 * Longest accepted answer, in UTF-16 code units (what `maxLength` counts). The
 * BFF and the iOS app use the same limit and unit.
 */
export const MAX_ADDITIONAL_CHECKOUT_FIELD_LENGTH = 100;

/** An address a shipping method always ships to (EP address keys). */
export type FixedShippingAddress = {
  line_1: string;
  line_2?: string;
  city: string;
  region: string;
  postcode: string;
  country: string;
};

const FIXED_ADDRESS_REQUIRED_KEYS = [
  'line_1',
  'city',
  'region',
  'postcode',
  'country'
] as const;

// Comma-separated flow-field value -> normalized list. EP has no array field type.
const parseCommaList = (raw: string | null | undefined): string[] =>
  (raw || '')
    .split(',')
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean);

/**
 * The label of the extra free-text field a shipping method requires at
 * checkout, or '' when it requires none. A non-empty label is what makes the
 * field required — there is no separate on/off flag.
 */
export const getAdditionalCheckoutFieldLabel = (
  method?: ShippingMethod
): string => (method?.additional_checkout_field_label || '').trim();

/** The small helper text shown under the extra field's input, or '' for none. */
export const getAdditionalCheckoutFieldHelperText = (
  method?: ShippingMethod
): string => (method?.additional_checkout_field_helper_text || '').trim();

/**
 * Reads a method's `fixed_shipping_address` (a JSON string of EP address keys;
 * EP flows have no object field type). Same rules as the BFF: `{}` when unset,
 * `{ address }` when usable, `{ invalid: true }` when set but unusable.
 */
export const parseFixedShippingAddress = (
  method?: ShippingMethod
): { address?: FixedShippingAddress; invalid?: true } => {
  const raw = (method?.fixed_shipping_address || '').trim();
  if (!raw) return {};
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { invalid: true };
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { invalid: true };
  }
  const value = (key: string) =>
    String((parsed as Record<string, unknown>)[key] ?? '').trim();
  if (FIXED_ADDRESS_REQUIRED_KEYS.some((key) => !value(key))) {
    return { invalid: true };
  }
  return {
    address: {
      line_1: value('line_1'),
      ...(value('line_2') && { line_2: value('line_2') }),
      city: value('city'),
      region: value('region'),
      postcode: value('postcode'),
      country: value('country')
    }
  };
};

/**
 * Drops methods this member can't use (VIVE-3848), matching the BFF's checks:
 * - `available_to_referrer_codes` lists the only EP `referrer_code`s allowed
 *   to use it; empty = every member. Any non-blank value restricts the method,
 *   even one that parses to no codes (e.g. a stray ","), so a half-edited
 *   entry fails closed. `referrer_code` is stored as typed at signup, so
 *   compare case-insensitively.
 * - a set-but-unusable `fixed_shipping_address` hides the method, since the
 *   BFF would reject it rather than ship to the member's own address.
 */
export const filterShippingMethodsForMember = (
  methods: ShippingMethod[],
  referrerCode: string | null | undefined
): ShippingMethod[] => {
  const code = (referrerCode || '').trim().toLowerCase();
  return methods.filter((method) => {
    if (parseFixedShippingAddress(method).invalid) return false;
    if (!(method.available_to_referrer_codes || '').trim()) return true;
    return parseCommaList(method.available_to_referrer_codes).includes(code);
  });
};
